// quality-management/services/mock/mockData.ts
// Mock data for Quality Management service (Projects, Tasks, Time Entries)

import { mockUsers, DEPARTMENTS, getUsersByDepartment } from '../../../shared/mock/mockData';
import type { Project, Task, TimeEntry } from '../api/types';

// Generate current date helpers
const today = new Date();
const getDateDaysAgo = (days: number): string => {
  const date = new Date(today);
  date.setDate(date.getDate() - days);
  return date.toISOString().split('T')[0];
};

const getDateDaysFromNow = (days: number): string => {
  const date = new Date(today);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};

// Mock Projects Data
export const mockProjects: Project[] = [
  // Reliability Department Projects
  {
    id: 1,
    name: 'System Reliability Enhancement',
    description: 'Improve system uptime and reduce failure rates across critical infrastructure components.',
    assigned_employee_ids: [3, 4, 5], // John, Jane, Mike (Reliability employees)
    created_by: 2, // Sarah (Reliability Manager)
    created_at: getDateDaysAgo(45),
    updated_at: getDateDaysAgo(2)
  },
  {
    id: 2,
    name: 'Database Performance Optimization',
    description: 'Optimize database queries and improve response times for core applications.',
    assigned_employee_ids: [4, 6, 7], // Jane, Lisa, Tom (Reliability employees)
    created_by: 2, // Sarah (Reliability Manager)
    created_at: getDateDaysAgo(30),
    updated_at: getDateDaysAgo(5)
  },
  {
    id: 3,
    name: 'Infrastructure Monitoring Setup',
    description: 'Implement comprehensive monitoring solution for all production systems.',
    assigned_employee_ids: [3, 5, 6], // John, Mike, Lisa (Reliability employees)
    created_by: 2, // Sarah (Reliability Manager)
    created_at: getDateDaysAgo(20),
    updated_at: getDateDaysAgo(1)
  },

  // Automation Department Projects
  {
    id: 4,
    name: 'CI/CD Pipeline Automation',
    description: 'Automate build, test, and deployment processes for faster delivery cycles.',
    assigned_employee_ids: [9, 10, 11], // Anna, David, Emma (Automation employees)
    created_by: 8, // Carlos (Automation Manager)
    created_at: getDateDaysAgo(40),
    updated_at: getDateDaysAgo(3)
  },
  {
    id: 5,
    name: 'Quality Assurance Automation',
    description: 'Implement automated testing frameworks and quality gates.',
    assigned_employee_ids: [10, 11, 12], // David, Emma, Ryan (Automation employees)
    created_by: 8, // Carlos (Automation Manager)
    created_at: getDateDaysAgo(25),
    updated_at: getDateDaysAgo(4)
  },
  {
    id: 6,
    name: 'DevOps Tool Integration',
    description: 'Integrate and streamline DevOps toolchain for better collaboration.',
    assigned_employee_ids: [9, 12, 13], // Anna, Ryan, Sofia (Automation employees)
    created_by: 8, // Carlos (Automation Manager)
    created_at: getDateDaysAgo(15),
    updated_at: getDateDaysAgo(1)
  }
];

// Mock Tasks Data
export const mockTasks: Task[] = [
  // Tasks for Project 1 (System Reliability Enhancement)
  {
    id: 1,
    project_id: 1,
    name: 'System Health Assessment',
    description: 'Conduct comprehensive assessment of current system health metrics.',
    assigned_employee_ids: [3], // John
    created_by: 2, // Sarah
    status: 'completed',
    created_date: getDateDaysAgo(45),
    completed_date: getDateDaysAgo(38),
    task_date: getDateDaysAgo(45),
    total_hours: 16
  },
  {
    id: 2,
    project_id: 1,
    name: 'Implement Failover Mechanisms',
    description: 'Design and implement automatic failover for critical services.',
    assigned_employee_ids: [4], // Jane
    created_by: 2, // Sarah
    status: 'in_progress',
    created_date: getDateDaysAgo(35),
    task_date: getDateDaysAgo(35),
    total_hours: 12
  },
  {
    id: 3,
    project_id: 1,
    name: 'Load Testing Framework',
    description: 'Create comprehensive load testing framework for stress testing.',
    assigned_employee_ids: [5], // Mike
    created_by: 2, // Sarah
    status: 'in_progress',
    created_date: getDateDaysAgo(25),
    task_date: getDateDaysAgo(25),
    total_hours: 8
  },

  // Tasks for Project 2 (Database Performance Optimization)
  {
    id: 4,
    project_id: 2,
    name: 'Query Performance Analysis',
    description: 'Analyze and identify slow-performing database queries.',
    assigned_employee_ids: [4], // Jane
    created_by: 2, // Sarah
    status: 'completed',
    created_date: getDateDaysAgo(30),
    completed_date: getDateDaysAgo(20),
    task_date: getDateDaysAgo(30),
    total_hours: 20
  },
  {
    id: 5,
    project_id: 2,
    name: 'Index Optimization',
    description: 'Optimize database indexes for improved query performance.',
    assigned_employee_ids: [6], // Lisa
    created_by: 2, // Sarah
    status: 'in_progress',
    created_date: getDateDaysAgo(18),
    task_date: getDateDaysAgo(18),
    total_hours: 15
  },
  {
    id: 6,
    project_id: 2,
    name: 'Database Migration Strategy',
    description: 'Plan and execute database migration with minimal downtime.',
    assigned_employee_ids: [7], // Tom
    created_by: 2, // Sarah
    status: 'in_progress',
    created_date: getDateDaysAgo(10),
    task_date: getDateDaysAgo(10),
    total_hours: 6
  },

  // Tasks for Project 4 (CI/CD Pipeline Automation)
  {
    id: 7,
    project_id: 4,
    name: 'Pipeline Architecture Design',
    description: 'Design the overall CI/CD pipeline architecture and workflow.',
    assigned_employee_ids: [9], // Anna
    created_by: 8, // Carlos
    status: 'completed',
    created_date: getDateDaysAgo(40),
    completed_date: getDateDaysAgo(28),
    task_date: getDateDaysAgo(40),
    total_hours: 24
  },
  {
    id: 8,
    project_id: 4,
    name: 'Automated Testing Integration',
    description: 'Integrate automated testing into the CI/CD pipeline.',
    assigned_employee_ids: [10], // David
    created_by: 8, // Carlos
    status: 'in_progress',
    created_date: getDateDaysAgo(25),
    task_date: getDateDaysAgo(25),
    total_hours: 18
  },
  {
    id: 9,
    project_id: 4,
    name: 'Deployment Automation',
    description: 'Automate deployment processes for different environments.',
    assigned_employee_ids: [11], // Emma
    created_by: 8, // Carlos
    status: 'in_progress',
    created_date: getDateDaysAgo(15),
    task_date: getDateDaysAgo(15),
    total_hours: 10
  },

  // Tasks for Project 5 (Quality Assurance Automation)
  {
    id: 10,
    project_id: 5,
    name: 'Test Framework Setup',
    description: 'Set up automated testing framework and infrastructure.',
    assigned_employee_ids: [10], // David
    created_by: 8, // Carlos
    status: 'in_progress',
    created_date: getDateDaysAgo(25),
    task_date: getDateDaysAgo(25),
    total_hours: 14
  },
  {
    id: 11,
    project_id: 5,
    name: 'Test Case Automation',
    description: 'Convert manual test cases to automated test scripts.',
    assigned_employee_ids: [11], // Emma
    created_by: 8, // Carlos
    status: 'in_progress',
    created_date: getDateDaysAgo(20),
    task_date: getDateDaysAgo(20),
    total_hours: 12
  },
  {
    id: 12,
    project_id: 5,
    name: 'Quality Gates Implementation',
    description: 'Implement quality gates and automated quality checks.',
    assigned_employee_ids: [12], // Ryan
    created_by: 8, // Carlos
    status: 'in_progress',
    created_date: getDateDaysAgo(12),
    task_date: getDateDaysAgo(12),
    total_hours: 8
  }
];

// Mock Time Entries Data (Last 30 days)
export const mockTimeEntries: TimeEntry[] = [
  // John's time entries (User ID: 3)
  { id: 1, employee_id: 3, task_id: 1, project_id: 1, date: getDateDaysAgo(5), hours: 8, description: 'Completed system health metrics analysis', created_at: getDateDaysAgo(5) },
  { id: 2, employee_id: 3, task_id: 3, project_id: 1, date: getDateDaysAgo(4), hours: 6, description: 'Working on load testing framework setup', created_at: getDateDaysAgo(4) },
  { id: 3, employee_id: 3, task_id: 3, project_id: 1, date: getDateDaysAgo(3), hours: 7, description: 'Implementing load testing scenarios', created_at: getDateDaysAgo(3) },
  { id: 4, employee_id: 3, task_id: 3, project_id: 1, date: getDateDaysAgo(2), hours: 8, description: 'Testing and validating load testing framework', created_at: getDateDaysAgo(2) },
  { id: 5, employee_id: 3, task_id: 3, project_id: 1, date: getDateDaysAgo(1), hours: 4, description: 'Documentation and code review', created_at: getDateDaysAgo(1) },

  // Jane's time entries (User ID: 4)
  { id: 6, employee_id: 4, task_id: 2, project_id: 1, date: getDateDaysAgo(5), hours: 7, description: 'Designing failover mechanisms', created_at: getDateDaysAgo(5) },
  { id: 7, employee_id: 4, task_id: 2, project_id: 1, date: getDateDaysAgo(4), hours: 8, description: 'Implementing primary failover logic', created_at: getDateDaysAgo(4) },
  { id: 8, employee_id: 4, task_id: 4, project_id: 2, date: getDateDaysAgo(3), hours: 6, description: 'Database query performance analysis', created_at: getDateDaysAgo(3) },
  { id: 9, employee_id: 4, task_id: 2, project_id: 1, date: getDateDaysAgo(2), hours: 5, description: 'Testing failover scenarios', created_at: getDateDaysAgo(2) },
  { id: 10, employee_id: 4, task_id: 2, project_id: 1, date: getDateDaysAgo(1), hours: 7, description: 'Finalizing failover implementation', created_at: getDateDaysAgo(1) },

  // Mike's time entries (User ID: 5)
  { id: 11, employee_id: 5, task_id: 3, project_id: 1, date: getDateDaysAgo(5), hours: 6, description: 'Research on load testing tools', created_at: getDateDaysAgo(5) },
  { id: 12, employee_id: 5, task_id: 3, project_id: 1, date: getDateDaysAgo(4), hours: 8, description: 'Setting up testing environment', created_at: getDateDaysAgo(4) },
  { id: 13, employee_id: 5, task_id: 3, project_id: 1, date: getDateDaysAgo(3), hours: 7, description: 'Creating test scenarios and scripts', created_at: getDateDaysAgo(3) },
  { id: 14, employee_id: 5, task_id: 3, project_id: 1, date: getDateDaysAgo(2), hours: 5, description: 'Running initial load tests', created_at: getDateDaysAgo(2) },
  { id: 15, employee_id: 5, task_id: 3, project_id: 1, date: getDateDaysAgo(1), hours: 6, description: 'Analyzing test results and optimization', created_at: getDateDaysAgo(1) },

  // Anna's time entries (User ID: 9) - Automation Department
  { id: 16, employee_id: 9, task_id: 7, project_id: 4, date: getDateDaysAgo(5), hours: 8, description: 'CI/CD pipeline architecture planning', created_at: getDateDaysAgo(5) },
  { id: 17, employee_id: 9, task_id: 7, project_id: 4, date: getDateDaysAgo(4), hours: 7, description: 'Tool evaluation and selection', created_at: getDateDaysAgo(4) },
  { id: 18, employee_id: 9, task_id: 7, project_id: 4, date: getDateDaysAgo(3), hours: 6, description: 'Pipeline configuration setup', created_at: getDateDaysAgo(3) },
  { id: 19, employee_id: 9, task_id: 7, project_id: 4, date: getDateDaysAgo(2), hours: 8, description: 'Integration testing of pipeline components', created_at: getDateDaysAgo(2) },
  { id: 20, employee_id: 9, task_id: 7, project_id: 4, date: getDateDaysAgo(1), hours: 5, description: 'Documentation and team training', created_at: getDateDaysAgo(1) },

  // David's time entries (User ID: 10) - Automation Department
  { id: 21, employee_id: 10, task_id: 8, project_id: 4, date: getDateDaysAgo(5), hours: 7, description: 'Automated testing tool research', created_at: getDateDaysAgo(5) },
  { id: 22, employee_id: 10, task_id: 8, project_id: 4, date: getDateDaysAgo(4), hours: 8, description: 'Test automation framework setup', created_at: getDateDaysAgo(4) },
  { id: 23, employee_id: 10, task_id: 10, project_id: 5, date: getDateDaysAgo(3), hours: 6, description: 'QA automation framework design', created_at: getDateDaysAgo(3) },
  { id: 24, employee_id: 10, task_id: 8, project_id: 4, date: getDateDaysAgo(2), hours: 7, description: 'Integration with CI pipeline', created_at: getDateDaysAgo(2) },
  { id: 25, employee_id: 10, task_id: 10, project_id: 5, date: getDateDaysAgo(1), hours: 6, description: 'Implementing test infrastructure', created_at: getDateDaysAgo(1) }
];

// Utility functions for Quality Management
export const getProjectsByDepartment = (department: 'Reliability' | 'Automation'): Project[] => {
  const departmentUsers = getUsersByDepartment(department);
  const managerIds = departmentUsers.filter(user => user.role === 'manager').map(user => user.id);
  
  return mockProjects.filter(project => managerIds.includes(project.created_by!));
};

export const getProjectsForUser = (userId: number): Project[] => {
  const user = mockUsers.find(u => u.id === userId);
  if (!user) return [];

  if (user.role === 'manager') {
    // Managers see all projects they created (their department projects)
    return mockProjects.filter(project => project.created_by === userId);
  } else {
    // Employees see projects they're assigned to
    return mockProjects.filter(project => 
      project.assigned_employee_ids.includes(userId)
    );
  }
};

export const getTasksForUser = (userId: number): Task[] => {
  const user = mockUsers.find(u => u.id === userId);
  if (!user) return [];

  if (user.role === 'manager') {
    // Managers see all tasks for their department projects
    const userProjects = getProjectsForUser(userId);
    const projectIds = userProjects.map(p => p.id);
    return mockTasks.filter(task => projectIds.includes(task.project_id));
  } else {
    // Employees see only tasks assigned to them
    return mockTasks.filter(task => 
      task.assigned_employee_ids.includes(userId)
    );
  }
};

export const getTasksByUserAndProject = (userId: number, projectId: number): Task[] => {
  const user = mockUsers.find(u => u.id === userId);
  if (!user) return [];

  if (user.role === 'manager') {
    // Managers see all tasks for the project
    return mockTasks.filter(task => task.project_id === projectId);
  } else {
    // Employees see only their assigned tasks for the project
    return mockTasks.filter(task => 
      task.project_id === projectId && 
      task.assigned_employee_ids.includes(userId)
    );
  }
};

export const getTimeEntriesForUser = (userId: number, startDate?: string, endDate?: string): TimeEntry[] => {
  let entries = mockTimeEntries.filter(entry => entry.employee_id === userId);
  
  if (startDate) {
    entries = entries.filter(entry => entry.date >= startDate);
  }
  
  if (endDate) {
    entries = entries.filter(entry => entry.date <= endDate);
  }
  
  return entries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

export const getDailyTimeEntries = (userId: number, date: string): TimeEntry[] => {
  return mockTimeEntries.filter(entry => 
    entry.employee_id === userId && entry.date === date
  );
};

export const getAvailableUsersForProject = (managerUserId: number): typeof mockUsers => {
  const manager = mockUsers.find(u => u.id === managerUserId);
  if (!manager || manager.role !== 'manager') return [];

  // Return employees from the same department who have management service access
  return mockUsers.filter(user => 
    user.department === manager.department &&
    user.role === 'employee' &&
    user.services.includes('management') &&
    user.is_active
  );
};

// Export all mock data
export const qualityManagementMockData = {
  projects: mockProjects,
  tasks: mockTasks,
  timeEntries: mockTimeEntries,
  
  // Utility functions
  getProjectsByDepartment,
  getProjectsForUser,
  getTasksForUser,
  getTasksByUserAndProject,
  getTimeEntriesForUser,
  getDailyTimeEntries,
  getAvailableUsersForProject
};
